import logoBanner from "@assets/images/logoBanner.png";
import { Card } from "@shared/components/Card";
import clsx from "clsx";
import { Suspense } from "react";
import { Link, Outlet } from "react-router-dom";

function AuthPageFallback() {
  return (
    <p className="text-sm text-gray-500" data-testid="auth-page-loading">
      Loading…
    </p>
  );
}

/**
 * Footer component with Terms of Service and Privacy Policy links.
 * Displays consistently across all authentication forms.
 */
function FooterLinks() {
  return (
    <div className="text-center">
      <div className="text-xs text-gray-500 space-x-4">
        <Link to="/terms" className="text-pink-600 hover:text-pink-700 font-medium" data-testid="terms-link">
          Terms of Service
        </Link>
        <Link to="/privacy" className="text-pink-600 hover:text-pink-700 font-medium" data-testid="privacy-link">
          Privacy Policy
        </Link>
      </div>
    </div>
  );
}

/**
 * Layout for phone (SMS) authentication. Child routes render inside the card via `<Outlet />`
 * (e.g. `PhoneAuthPage` at `/auth`).
 *
 * Features:
 * - Consistent logo banner
 * - Footer with Terms of Service and Privacy Policy links
 * - Responsive design with proper spacing
 *
 * @example
 * ```tsx
 * // Router: AuthRoute → AuthLayout → path "auth" → PhoneAuthPage
 * <Outlet />
 * ```
 */
export function AuthLayout() {
  return (
    <div
      className={clsx(
        // Base layout & dimensions
        "min-h-screen flex flex-col items-center justify-center p-4",
        // Spacing
        "space-y-16",
        // Visual appearance & backgrounds
        "bg-lavender-400"
      )}
    >
      <div className="text-center">
        <img
          src={logoBanner}
          alt="Fitties Logo"
          className="w-full max-logo-banner-width mx-auto"
          data-testid="logo-banner"
        />
      </div>

      <main className="w-full max-w-xl">
        <Card className="text-center space-y-6">
          <Suspense fallback={<AuthPageFallback />}>
            <Outlet />
          </Suspense>
          <FooterLinks />
        </Card>
      </main>
    </div>
  );
}
